import { useApolloClient } from '@apollo/client';
import { Align, ListItem, Row, Theme } from '@busybusy/webapp-react-ui';
import FlexContainer from '@busybusy/webapp-react-ui/dist/components/FlexContainer/FlexContainer';
import classNames from 'classnames';
import OptionalUnwrapper from 'components/foundation/OptionalUnwrapperContainer/OptionalUnwrapper';
import DashboardList from 'components/foundation/dashboard/dashboard-list/DashboardList/DashboardList';
import Typography from 'components/foundation/text/Typography/Typography';
import { LOG_FEED_TIME_ENTRY_LOGS_QUERY } from 'containers/time-log-feed/apollo/time-log-feed-queries';
import TimeEntryLogEventDialogCoordinator from 'containers/time-log-feed/dialogs/TimeEntryLogEventDialogCoordinator/TimeEntryLogEventDialogCoordinator';
import { ITimeLogFeedTableRow } from 'containers/time-log-feed/table/TimeLogFeedTable/TimeLogFeedTable';
import useTimeLogFeedDataToRowConverter from 'containers/time-log-feed/table/hooks/useTimeLogFeedDataToRowConverter/useTimeLogFeedDataToRowConverter';
import { ILogFeedTimeGrouping } from 'containers/time-log-feed/types/types';
import { useOpenable } from 'hooks';
import { first } from 'lodash';
import { useState } from 'react';
import { ClassName } from 'types/ClassName';
import { Nullable } from 'types/util/Nullable';
import { ITimeLogFeedDashboardRow } from '../hooks/useTimeLogFeedDashboardDataMapper/useTimeLogFeedDashboardDataMapper';
import './TimeLogFeedDashboardCardList.scss';

export interface ITimeLogFeedDashboardCardListProps {
  data: ITimeLogFeedDashboardRow[];
  theme: Theme.DARK | Theme.LIGHT;
  reload: () => Promise<void>;
  className?: ClassName;
}

function TimeLogFeedDashboardCardList({ data, theme, reload, className }: ITimeLogFeedDashboardCardListProps) {
  const timeLogEventDetails = useOpenable();
  const client = useApolloClient();

  const rowConverter = useTimeLogFeedDataToRowConverter();
  const [activeRow, setActiveRow] = useState<Nullable<ITimeLogFeedTableRow>>(null);

  async function openGroupingDetail(grouping: ILogFeedTimeGrouping) {
    // Fetch the full log for the details dialog, but keep any failure contained to this row — the onClick
    // handler doesn't await, so a thrown error would otherwise surface as an unhandled rejection. Fall
    // back to the summary grouping already in hand if the detail fetch fails.
    let groupingToShow = grouping;
    try {
      const result = await client.query<{ clientTimeEntryLogs: ILogFeedTimeGrouping['timeEntryLog'][] }>({
        query: LOG_FEED_TIME_ENTRY_LOGS_QUERY,
        variables: { first: 1, filter: { id: { equal: grouping.timeEntryLog.id } } },
        fetchPolicy: 'network-only',
      });
      const fullLog = first(result.data?.clientTimeEntryLogs ?? []);
      if (fullLog) {
        groupingToShow = { ...grouping, timeEntryLog: fullLog };
      }
    } catch {
      // Detail fetch failed — fall back to the summary row.
    }

    try {
      setActiveRow(await rowConverter(groupingToShow));
      timeLogEventDetails.open();
    } catch {
      // Row conversion failed; leave the dialog closed rather than surfacing an unhandled rejection.
    }
  }

  const classes = classNames('time-log-feed-dashboard-card-list', theme, className);

  return (
    <>
      <DashboardList
        className={classes}
        rowItems={data ?? []}
        theme={theme}
        hover={false}
        renderItem={(item) => (
          <ListItem className={theme} onClick={item.grouping ? () => openGroupingDetail(item.grouping!) : undefined}>
            <Row align={Align.CENTER}>
              <FlexContainer className="full-width" justifyContent="space-between">
                <div className="time-log-feed-left-content">
                  <Typography tag="div" fontWeight="semiBold" className="time-log-feed-label">
                    {item.label}
                  </Typography>
                  <OptionalUnwrapper value={item.sublabel}>
                    {(sublabel) => (
                      <Typography tag="div" className="time-log-feed-sublabel">
                        {sublabel}
                      </Typography>
                    )}
                  </OptionalUnwrapper>
                </div>

                <Typography tag="div" className="time-log-feed-value">
                  {item.value}
                </Typography>
              </FlexContainer>
            </Row>
          </ListItem>
        )}
      />
      <OptionalUnwrapper value={activeRow}>
        {(row) => (
          <TimeEntryLogEventDialogCoordinator
            onTimeEntryChange={reload}
            position="center"
            row={row}
            isOpen={timeLogEventDetails.isOpen}
            onClose={timeLogEventDetails.close}
          />
        )}
      </OptionalUnwrapper>
    </>
  );
}

export default TimeLogFeedDashboardCardList;
